/* eslint-disable react/no-danger */
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useSelectPrize } from '../../hooks/useParticipation';
import * as AppPropTypes from '../../lib/PropTypes';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import Prize from './Prize';
import PrizesSelector from './PrizesSelector';

import styles from '../../../styles/partials/game-win.module.scss';

const propTypes = {
    game: AppPropTypes.game,
    participation: AppPropTypes.participation,
    className: PropTypes.string,
};

const defaultProps = {
    game: null,
    participation: null,
    className: null,
};

function GameWin({ game, participation, className }) {
    const themeClassName = useThemeClassName(styles);
    const { gameWinTitle } = useLabels();
    const { id: participationId = null, prizes = null } = participation || {};
    const tracking = useTracking();

    const selectablePrizes = useMemo(
        () => (prizes || []).filter(({ selectable }) => selectable),
        [prizes],
    );
    const hasSelectablePrizes = selectablePrizes.length > 0;
    const { length: prizesLength = 0 } = prizes || [];

    const [selectPrizeTransitionning, setSelectPrizeTransitionning] = useState(false);

    const { selectPrizeAsync } = useSelectPrize(participation);
    const selectPrize = useCallback(
        (prize) => {
            const { uuid } = prize || {};
            selectPrizeAsync(uuid).then(
                ({ participant: updatedUser, participation: updatedParticipation }) => {
                    tracking.trackInteraction('game_select_prize', {
                        game,
                        participant: updatedUser,
                        participation: updatedParticipation,
                        prize,
                    });
                },
            );
        },
        [game, participationId, tracking],
    );

    const onSelectPrizeTransitionStart = useCallback(() => {
        setSelectPrizeTransitionning(true);
    }, []);

    const onSelectPrizeTransitionEnd = useCallback(() => {
        setSelectPrizeTransitionning(false);
    }, []);

    const showSelector = hasSelectablePrizes || selectPrizeTransitionning;

    if (!showSelector && prizesLength === 0) {
        return null;
    }

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.inner}>
                {showSelector ? (
                    <PrizesSelector
                        className={styles.prizesSelector}
                        prizes={selectablePrizes}
                        selectPrize={selectPrize}
                        onSelectTransitionStart={onSelectPrizeTransitionStart}
                        onSelectTransitionEnd={onSelectPrizeTransitionEnd}
                    />
                ) : (
                    <div className={styles.prizesContainer}>
                        {gameWinTitle !== false ? (
                            <h4 className={styles.prizesTitle}>
                                {gameWinTitle || (
                                    <FormattedMessage
                                        defaultMessage="{count, plural, =0 {Well done!} one {Your prize} other {Your prizes}}"
                                        values={{ count: prizesLength }}
                                        description="GameWin prizes title"
                                    />
                                )}
                            </h4>
                        ) : null}
                        <div className={styles.prizes}>
                            {(prizes || []).map((prize) => {
                                const { uuid = null } = prize || {};
                                return (
                                    <Prize
                                        className={styles.prize}
                                        key={`prize-${uuid}`}
                                        prize={prize}
                                        withoutHeader
                                        onPrizeClaim={selectPrize}
                                        withAccountLink
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

GameWin.propTypes = propTypes;
GameWin.defaultProps = defaultProps;

export default GameWin;
